/**
 * Map of Systems — Dashboard card (POW-197).
 *
 * Ported from powow-group-management's Map of Systems page (frontend/src/pages/
 * MapOfSystems.js): same base map, cloud layer, tile settings and colours. Differences:
 *   - data comes from /api/dashboard/map/*, scoped to the user's own systems
 *   - clicking a system OR a station opens its cluster's weather and region price
 *   - no clusters table / detail drawer (out of scope for the card)
 *   - every value written into Leaflet's HTML is escaped
 *
 * Smoothness (measured): one canvas renderer for the map's lifetime (a renderer per
 * redraw left an extra canvas behind on every filter change, all repainted on each
 * pan), one shared hover label instead of a tooltip per system, and station links
 * drawn only for the selected station instead of one line per system.
 */
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Card, Button, Spin, Space, Segmented, Tooltip, Tag, Typography, theme } from "antd";
import {
  ReloadOutlined, EnvironmentOutlined, CloudOutlined, AimOutlined, CloseOutlined, WarningOutlined,
} from "@ant-design/icons";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/libs/api";

const { Text } = Typography;

// Distance to the weather station, light → dark (reference's validated ramp).
const BANDS = [
  { max: 3, colour: "#86b6ef", label: "≤ 3 km" },
  { max: 6, colour: "#5598e7", label: "3–6 km" },
  { max: 10, colour: "#2a78d6", label: "6–10 km" },
  { max: 15, colour: "#1c5cab", label: "10–15 km" },
  { max: Infinity, colour: "#104281", label: "> 15 km" },
];
const UNASSIGNED = "#ec835a";
const CENTRE_INK = "#52514e";
const SURFACE = "#fcfcfb";
const bandOf = (km) => (km == null ? BANDS[0] : BANDS.find((b) => km <= b.max));

const STATE_ORDER = ["SA", "NSW", "QLD", "VIC", "ACT", "NT", "TAS"];
const EXCLUDED_STATES = new Set(["WA", "OUT"]);

// Himawari-9 via NASA GIBS (no key). maxNativeZoom must match what GIBS holds.
const GIBS = "https://gibs.earthdata.nasa.gov/wmts/epsg3857/best";
const CLOUD_LAYERS = {
  visible: { id: "Himawari_AHI_Band3_Red_Visible_1km", maxNativeZoom: 7, label: "visible", className: "cloud-overlay-visible", opacity: 0.9 },
  infrared: { id: "Himawari_AHI_Band13_Clean_Infrared", maxNativeZoom: 6, label: "infrared", className: "cloud-overlay-infrared", opacity: 1 },
};
const CLOUD_LAG_MINUTES = 40;
/** Latest 10-minute GIBS frame that has landed (GIBS runs behind real time). */
function latestCloudFrame() {
  const t = new Date(Date.now() - CLOUD_LAG_MINUTES * 60 * 1000);
  t.setUTCMinutes(Math.floor(t.getUTCMinutes() / 10) * 10, 0, 0);
  return t;
}
/** Visible band while any of Australia is lit (~20:00–10:00 UTC), infrared otherwise. */
const cloudBandFor = (date) => {
  const h = date.getUTCHours();
  return h >= 20 || h < 10 ? "visible" : "infrared";
};

const GA_BASEMAP = "https://services.ga.gov.au/gis/rest/services/NationalBaseMap/MapServer/tile/{z}/{y}/{x}";
const AU_CENTRE = [-30, 135];
const AU_BOUNDS = L.latLngBounds([-44.5, 112.0], [-9.0, 154.5]);
const CAPITAL = {
  SA: [-34.9285, 138.6007], NSW: [-33.8688, 151.2093], QLD: [-27.4698, 153.0251],
  VIC: [-37.8136, 144.9631], ACT: [-35.2809, 149.13], NT: [-12.4634, 130.8456], TAS: [-42.8821, 147.3272],
};
const ZOOM_IN_LEVELS = 3;
const ZOOM_IN_BY = { ACT: 0 };
const MAX_ZOOM = 18;
const MAP_HEIGHT = 560;
const CONDITIONS_TTL_MS = 5 * 60 * 1000;

const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]));
const fmtTemp = (v) => (v == null ? "—" : `${Number(v).toFixed(1)}°`);
const fmtPrice = (v) => (v == null ? "—" : `${v < 0 ? "-$" : "$"}${Math.abs(v).toFixed(2)}`);
const hhmm = (aest) => (aest ? String(aest).slice(11, 16) : "");
const n = (v) => Number(v || 0).toLocaleString();
/** "1 system" / "4,570 systems" */
const count = (v, word) => `${n(v)} ${word}${Number(v) === 1 ? "" : "s"}`;

/** Popup: fixed head (cluster facts) + live body (weather, price). Leaflet popups are HTML strings. */
function popupHtml(c, cond) {
  const head = `<div class="mos-pop-head"><div class="mos-pop-title">${esc(c.cluster)}</div>`
    + `<div class="mos-pop-sub">Weather station · ${esc(c.state)}${c.clusterId != null ? ` · #${esc(c.clusterId)}` : ""}</div></div>`
    + `<div class="mos-pop-facts">`
    + `<div><span>${esc(n(c.systems))}</span>your system${Number(c.systems) === 1 ? "" : "s"}</div>`
    + `<div><span>${esc(c.kmMean)} km</span>mean distance</div>`
    + `<div><span>${esc(c.kmMax)} km</span>furthest</div></div>`;

  let body;
  if (!cond || cond.loading) {
    body = `<div class="mos-pop-section"><div class="mos-skel" style="width:60%"></div><div class="mos-skel" style="width:85%"></div></div>`
      + `<div class="mos-pop-section"><div class="mos-skel" style="width:45%"></div></div>`;
  } else if (cond.error) {
    body = `<div class="mos-pop-section mos-pop-error">${esc(cond.error)}</div>`;
  } else {
    const d = cond.data;
    const w = d.weather;
    const weather = w
      ? `<div class="mos-pop-section mos-pop-weather">`
        + `${w.iconUrl ? `<img src="${esc(w.iconUrl)}" alt="">` : ""}`
        + `<div><div class="mos-pop-temp">${esc(fmtTemp(w.tempC))}<small>${esc(w.conditionText || "")}</small></div>`
        + `<div class="mos-pop-meta">UV ${esc(w.uv ?? "—")} · cloud ${w.cloud == null ? "—" : `${esc(w.cloud)}%`} · ${esc(w.source)} ${esc(hhmm(w.aest))} AEST</div></div></div>`
      : `<div class="mos-pop-section mos-pop-muted">${esc(d.weatherReason || "Weather unavailable")}</div>`;
    const p = d.price || {};
    const price = p.region && p.latest
      ? `<div class="mos-pop-section mos-pop-price"><span class="mos-pop-label">Spot price · ${esc(p.region)}</span>`
        + `<span class="mos-pop-value">${esc(fmtPrice(p.latest.rrp))}<small>/MWh</small></span>`
        + `<span class="mos-pop-meta">settled ${esc(hhmm(p.latest.aest))} AEST</span></div>`
      : `<div class="mos-pop-section mos-pop-price"><span class="mos-pop-label">Spot price${p.region ? ` · ${esc(p.region)}` : ""}</span>`
        + `<span class="mos-pop-muted">${esc(p.reason || "No spot price")}</span></div>`;
    body = weather + price;
  }
  return `<div class="mos-pop">${head}${body}</div>`;
}

const CSS = `
.basemap-muted { filter: grayscale(0.85) brightness(1.08) contrast(0.88); }
.cloud-overlay-visible { mix-blend-mode: screen; filter: url(#cloud-sharpen) contrast(1.5) brightness(1.05); }
.cloud-overlay-infrared { filter: url(#cloud-chroma); }
.mos-map .leaflet-container { font-family: inherit; background: #eef1f4; }
.mos-map .leaflet-control-zoom { border: none; box-shadow: 0 1px 4px rgba(0,0,0,.15); border-radius: 8px; overflow: hidden; }
.mos-map .leaflet-control-zoom a { width: 32px; height: 32px; line-height: 32px; color: #262B40; }
.mos-map .leaflet-control-attribution { font-size: 10px; background: rgba(255,255,255,.75); }
.mos-tip.leaflet-tooltip { border: none; border-radius: 8px; box-shadow: 0 4px 14px rgba(0,0,0,.15); padding: 6px 10px; font-size: 12px; line-height: 1.5; color: #262B40; }
.mos-tip b { font-weight: 600; }
.mos-tip .muted { color: #6b7280; }
.mos-popup .leaflet-popup-content-wrapper { border-radius: 12px; box-shadow: 0 8px 28px rgba(0,0,0,.18); padding: 0; }
.mos-popup .leaflet-popup-content { margin: 0; width: 280px !important; }
.mos-popup .leaflet-popup-close-button { top: 8px !important; right: 8px !important; color: #9ca3af; }
.mos-pop { font-size: 12px; color: #262B40; }
.mos-pop-head { padding: 12px 36px 8px 14px; }
.mos-pop-title { font-size: 15px; font-weight: 600; line-height: 1.3; }
.mos-pop-sub { color: #6b7280; font-size: 11px; margin-top: 2px; }
.mos-pop-facts { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px; padding: 0 14px 10px; color: #6b7280; font-size: 10.5px; }
.mos-pop-facts span { display: block; color: #262B40; font-size: 13px; font-weight: 600; }
.mos-pop-section { border-top: 1px solid #f0f1f3; padding: 10px 14px; min-height: 22px; }
.mos-pop-weather { display: flex; align-items: center; gap: 8px; }
.mos-pop-weather img { width: 40px; height: 40px; margin: -4px 0; }
.mos-pop-temp { font-size: 22px; font-weight: 600; line-height: 1.1; }
.mos-pop-temp small { font-size: 12px; font-weight: 400; color: #4A5073; margin-left: 8px; }
.mos-pop-meta { color: #6b7280; font-size: 11px; margin-top: 2px; display: block; }
.mos-pop-price { display: grid; grid-template-columns: 1fr auto; align-items: baseline; row-gap: 2px; }
.mos-pop-label { color: #6b7280; font-size: 11px; }
.mos-pop-value { font-size: 16px; font-weight: 600; text-align: right; }
.mos-pop-value small { font-size: 11px; font-weight: 400; color: #6b7280; margin-left: 2px; }
.mos-pop-price .mos-pop-meta, .mos-pop-price .mos-pop-muted { grid-column: 1 / -1; }
.mos-pop-muted { color: #6b7280; }
.mos-pop-error { color: #cf1322; }
.mos-skel { height: 10px; border-radius: 5px; margin: 5px 0; background: linear-gradient(90deg,#f0f1f3 25%,#e4e6ea 37%,#f0f1f3 63%); background-size: 400% 100%; animation: mos-shimmer 1.2s ease infinite; }
@keyframes mos-shimmer { 0% { background-position: 100% 50% } 100% { background-position: 0 50% } }
.mos-panel { position: absolute; z-index: 600; background: rgba(255,255,255,.94); border-radius: 10px; box-shadow: 0 2px 10px rgba(0,0,0,.12); backdrop-filter: blur(4px); }
.mos-fade { transition: opacity .25s ease; }
`;

export default function MapOfSystemsCard() {
  const { token } = theme.useToken();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [inView, setInView] = useState(false);
  const [stateFilter, setStateFilter] = useState("SA");
  const [showRings, setShowRings] = useState(true);
  const [showClouds, setShowClouds] = useState(false);
  const [cloudFrame, setCloudFrame] = useState(null);
  const [selected, setSelected] = useState(null);

  const cardRef = useRef(null);
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const rendererRef = useRef(null);  // ONE canvas renderer for the map's lifetime
  const layerRef = useRef(null);
  const linksRef = useRef(null);     // links of the selected station only
  const cloudLayerRef = useRef(null);
  const hoverRef = useRef(null);     // one shared hover label
  const marksRef = useRef({ sites: [], rings: [], stations: [] });
  const selectedRef = useRef(null);
  const visibleRef = useRef({ points: [] });
  const clusterOfRef = useRef(new Map());
  const conditionsRef = useRef({}); // cluster → { loading | error | data, at }

  // Load only once the card is on screen (the data is ~1 MB for a platform admin).
  useEffect(() => {
    const el = cardRef.current;
    if (!el || typeof IntersectionObserver === "undefined") { setInView(true); return undefined; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setInView(true); io.disconnect(); }
    }, { rootMargin: "200px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/dashboard/map/points${refresh ? "?refresh=1" : ""}`, { timeout: 120000 });
      if (!res.data?.success) throw new Error(res.data?.message || "Could not load the map data");
      setData(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Could not load the map data");
    }
    setLoading(false);
  }, []);

  useEffect(() => { if (inView) load(); }, [inView, load]);

  // Open on whichever state holds most of the user's systems.
  useEffect(() => {
    if (!data) return;
    const best = STATE_ORDER.filter((s) => data.byState[s]).sort((a, b) => data.byState[b] - data.byState[a])[0];
    if (best) setStateFilter(best);
  }, [data]);

  const visible = useMemo(() => {
    if (!data) return { points: [], centres: [] };
    if (stateFilter === "ALL") {
      return {
        points: data.points.filter((p) => !EXCLUDED_STATES.has(p.state)),
        centres: data.centres.filter((c) => !EXCLUDED_STATES.has(c.state)),
      };
    }
    return {
      points: data.points.filter((p) => p.state === stateFilter),
      centres: data.centres.filter((c) => c.state === stateFilter),
    };
  }, [data, stateFilter]);

  const clustersInView = useMemo(() => {
    if (!data) return [];
    return stateFilter === "ALL"
      ? data.clusters.filter((c) => !EXCLUDED_STATES.has(c.state))
      : data.clusters.filter((c) => c.state === stateFilter);
  }, [data, stateFilter]);

  /** Weather + price for one station, cached per cluster for 5 minutes. */
  const askConditions = useCallback(async (cluster) => {
    const held = conditionsRef.current[cluster];
    if (held && (held.loading || (held.data && Date.now() - held.at < CONDITIONS_TTL_MS))) return held;
    const put = (next) => { conditionsRef.current = { ...conditionsRef.current, [cluster]: next }; return next; };
    put({ loading: true });
    try {
      const res = await api.get("/api/dashboard/map/station", { params: { cluster }, timeout: 30000 });
      return put({ data: res.data.data, at: Date.now() });
    } catch (err) {
      return put({ error: err.response?.data?.message || "Could not read the weather and price" });
    }
  }, []);

  const openClusterPopup = useCallback((latlng, c) => {
    const map = mapRef.current;
    if (!map) return;
    const popup = L.popup({ className: "mos-popup", minWidth: 280, maxWidth: 280, autoPanPaddingTopLeft: [72, 24], autoPanPaddingBottomRight: [24, 24] })
      .setLatLng(latlng)
      .setContent(popupHtml(c, conditionsRef.current[c.cluster]))
      .openOn(map);
    askConditions(c.cluster).then((cond) => { if (map.hasLayer(popup)) popup.setContent(popupHtml(c, cond)); });
  }, [askConditions]);

  /** Dim everything outside the selected cluster and draw only its links (restyle in place). */
  const applySelection = useCallback((cluster) => {
    const map = mapRef.current;
    const { sites, rings, stations } = marksRef.current;
    const dim = !!cluster;
    for (const m of sites) {
      const on = !dim || m.cluster === cluster;
      m.layer.setStyle({ fillOpacity: on ? 0.95 : 0.12, opacity: on ? 1 : 0.12, weight: on && dim ? 1.5 : 1 });
    }
    for (const m of rings) {
      const on = !dim || m.cluster === cluster;
      m.layer.setStyle({ opacity: on ? (dim ? 0.85 : 0.22) : 0.05, weight: on && dim ? 2 : 1, fillOpacity: on && dim ? 0.07 : 0.03 });
    }
    for (const m of stations) {
      const on = !dim || m.cluster === cluster;
      m.layer.setStyle({ fillOpacity: on ? 1 : 0.2, opacity: on ? 1 : 0.2 });
    }
    if (!map) return;
    if (linksRef.current) { map.removeLayer(linksRef.current); linksRef.current = null; }
    const c = cluster && clusterOfRef.current.get(cluster);
    if (!c) return;
    const links = L.layerGroup();
    for (const p of visibleRef.current.points) {
      if (p.cluster !== cluster) continue;
      L.polyline([[p.lat, p.lon], [c.lat, c.lon]], {
        renderer: rendererRef.current, color: CENTRE_INK, weight: 0.8, opacity: 0.45, interactive: false,
      }).addTo(links);
    }
    links.addTo(map);
    linksRef.current = links;
  }, []);

  // Map, renderer and hover label: created once, destroyed on unmount.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;
    const map = L.map(containerRef.current, {
      preferCanvas: true,
      zoomControl: true,
      maxBounds: AU_BOUNDS.pad(0.35),
      maxBoundsViscosity: 0.5,
      wheelPxPerZoomLevel: 120,
    }).setView(AU_CENTRE, 4);
    L.tileLayer(GA_BASEMAP, {
      maxZoom: 19,
      maxNativeZoom: 16,
      bounds: AU_BOUNDS.pad(0.35),
      className: "basemap-muted",
      attribution: "&copy; Geoscience Australia (CC BY 4.0), &copy; OpenStreetMap contributors",
    }).addTo(map);
    rendererRef.current = L.canvas({ padding: 0.5, tolerance: 4 });
    hoverRef.current = L.tooltip({ className: "mos-tip", direction: "top", offset: [0, -6] });
    const clear = () => setSelected(null);
    map.on("click", clear);
    mapRef.current = map;
    return () => {
      map.off("click", clear);
      map.remove();
      mapRef.current = null;
      rendererRef.current = null;
      layerRef.current = null;
      linksRef.current = null;
      cloudLayerRef.current = null;
    };
  }, []);

  // Markers: rebuilt when the data, state or the catchment toggle changes — on the same renderer.
  useEffect(() => {
    const map = mapRef.current;
    const renderer = rendererRef.current;
    if (!map || !renderer || !data) return;
    if (layerRef.current) { map.removeLayer(layerRef.current); layerRef.current = null; }
    map.closeTooltip(hoverRef.current);

    const group = L.layerGroup();
    const clusterOf = new Map(data.clusters.map((c) => [c.cluster, c]));
    clusterOfRef.current = clusterOf;
    visibleRef.current = visible;
    const marks = { sites: [], rings: [], stations: [] };
    const hover = (latlng, html) => { hoverRef.current.setLatLng(latlng).setContent(html); map.openTooltip(hoverRef.current); };
    const unhover = () => map.closeTooltip(hoverRef.current);

    if (showRings) {
      for (const c of visible.centres) {
        marks.rings.push({
          cluster: c.cluster,
          layer: L.circle([c.lat, c.lon], {
            radius: (data.radiusKm || 20) * 1000, renderer, color: CENTRE_INK, weight: 1, opacity: 0.22,
            fillColor: CENTRE_INK, fillOpacity: 0.03, interactive: false,
          }).addTo(group),
        });
      }
    }
    for (const p of visible.points) {
      const c = p.unassigned ? null : clusterOf.get(p.cluster);
      const layer = L.circleMarker([p.lat, p.lon], {
        renderer, radius: 3.5, color: SURFACE, weight: 1,
        fillColor: p.unassigned ? UNASSIGNED : bandOf(p.km).colour, fillOpacity: 0.95,
        bubblingMouseEvents: false,
      }).addTo(group);
      layer.on("mouseover", () => hover([p.lat, p.lon],
        `<b>${esc(p.sn || "unknown")}</b>${p.nmi ? ` <span class="muted">· NMI ${esc(p.nmi)}</span>` : ""}<br>`
        + (p.unassigned ? `<span class="muted">No weather station assigned</span>`
          : `${esc(p.cluster)} <span class="muted">· ${esc(p.km)} km away</span>`)));
      layer.on("mouseout", unhover);
      // Clicking a system shows its cluster's weather and region price (POW-197).
      if (c) layer.on("click", (e) => { setSelected(p.cluster); openClusterPopup(e.latlng, c); });
      marks.sites.push({ cluster: p.cluster || null, layer });
    }
    for (const c of clustersInView) {
      const layer = L.circleMarker([c.lat, c.lon], {
        renderer, radius: 4.5, color: SURFACE, weight: 2, fillColor: CENTRE_INK, fillOpacity: 1,
        bubblingMouseEvents: false,
      }).addTo(group);
      layer.on("mouseover", () => hover([c.lat, c.lon], `<b>${esc(c.cluster)}</b><br><span class="muted">${esc(count(c.systems, "system"))} · weather station</span>`));
      layer.on("mouseout", unhover);
      layer.on("click", (e) => { setSelected(c.cluster); openClusterPopup(e.latlng, c); });
      marks.stations.push({ cluster: c.cluster, layer });
    }

    group.addTo(map);
    layerRef.current = group;
    marksRef.current = marks;
    applySelection(selectedRef.current);
  }, [data, visible, clustersInView, showRings, applySelection, openClusterPopup]);

  useEffect(() => { selectedRef.current = selected; applySelection(selected); }, [selected, applySelection]);
  useEffect(() => { setSelected(null); if (mapRef.current) mapRef.current.closePopup(); }, [stateFilter]);

  // Camera: only on data / state change, never on a layer toggle.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !data) return;
    const pts = visible.points.map((p) => [p.lat, p.lon]);
    if (!pts.length) { map.setView(AU_CENTRE, 4); return; }
    const bounds = L.latLngBounds(pts).pad(0.08);
    const capital = CAPITAL[stateFilter];
    // Instant, not flyTo: a fly animation repaints every system on every frame (measured 7.9 s freeze for All).
    if (!capital) { map.fitBounds(bounds); return; }
    const fitted = map.getBoundsZoom(bounds);
    map.setView(capital, Math.min(fitted + (ZOOM_IN_BY[stateFilter] ?? ZOOM_IN_LEVELS), MAX_ZOOM));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, stateFilter]);

  // Cloud overlay: its own layer, refreshed every 10 minutes while switched on.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;
    if (!showClouds) {
      if (cloudLayerRef.current) { map.removeLayer(cloudLayerRef.current); cloudLayerRef.current = null; }
      setCloudFrame(null);
      return undefined;
    }
    const add = () => {
      const when = latestCloudFrame();
      const band = CLOUD_LAYERS[cloudBandFor(when)];
      const stamp = when.toISOString().replace(/\.\d{3}Z$/, "Z");
      const layer = L.tileLayer(
        `${GIBS}/${band.id}/default/${stamp}/GoogleMapsCompatible_Level${band.maxNativeZoom}/{z}/{y}/{x}.png`,
        {
          maxZoom: 19,
          maxNativeZoom: band.maxNativeZoom,
          opacity: band.opacity,
          zIndex: 2,
          errorTileUrl: "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7",
          className: band.className,
          attribution: "Cloud: Himawari-9 via NASA GIBS",
        },
      );
      // Swap only once the new frame has loaded, so a refresh never blinks to no cloud.
      const prev = cloudLayerRef.current;
      layer.once("load", () => { if (prev && map.hasLayer(prev)) map.removeLayer(prev); });
      layer.addTo(map);
      cloudLayerRef.current = layer;
      setCloudFrame({ at: when, band: band.label });
    };
    add();
    const timer = setInterval(add, 10 * 60 * 1000);
    return () => {
      clearInterval(timer);
      if (cloudLayerRef.current && mapRef.current) mapRef.current.removeLayer(cloudLayerRef.current);
      cloudLayerRef.current = null;
    };
  }, [showClouds]);

  // Leaflet measures its container once; re-measure when the card resizes (sidebar collapse etc.).
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    let frame = null;
    const ro = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;
      if (!box || box.width === 0 || box.height === 0) return;
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => { frame = null; if (mapRef.current) mapRef.current.invalidateSize(); });
    });
    ro.observe(el);
    return () => { if (frame) cancelAnimationFrame(frame); ro.disconnect(); };
  }, []);

  const stateOptions = useMemo(() => {
    if (!data) return [{ label: "All", value: "ALL" }];
    const inAu = STATE_ORDER.filter((s) => data.byState[s]);
    return [
      ...inAu.map((s) => ({ label: <span>{s} <Text type="secondary" style={{ fontSize: 11 }}>{n(data.byState[s])}</Text></span>, value: s })),
      { label: <span>All <Text type="secondary" style={{ fontSize: 11 }}>{n(inAu.reduce((a, s) => a + data.byState[s], 0))}</Text></span>, value: "ALL" },
    ];
  }, [data]);

  const selectedCluster = selected ? data?.clusters.find((x) => x.cluster === selected) : null;
  const notPlotted = data ? data.outliers.length + (data.byState.WA || 0) : 0;
  const busy = loading || !inView;

  const layerToggle = (on, set, icon, label, tip) => (
    <Tooltip title={tip}>
      <Button size="small" type={on ? "primary" : "default"} ghost={on} icon={icon} onClick={() => set(!on)}>{label}</Button>
    </Tooltip>
  );

  return (
    <div ref={cardRef}>
      {/* SVG filters the cloud layers reference via CSS url(#id): they must be in the document. */}
      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
        <defs>
          <filter id="cloud-sharpen" colorInterpolationFilters="sRGB">
            <feConvolveMatrix order="3" preserveAlpha="true" kernelMatrix="0 -0.85 0 -0.85 4.4 -0.85 0 -0.85 0" />
          </filter>
          <filter id="cloud-chroma" colorInterpolationFilters="sRGB">
            <feConvolveMatrix order="3" preserveAlpha="true" kernelMatrix="0 -0.85 0 -0.85 4.4 -0.85 0 -0.85 0" result="sharp" />
            <feColorMatrix in="sharp" type="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  -2 1 1 0 0" />
          </filter>
        </defs>
      </svg>
      <style>{CSS}</style>

      <Card
        variant="borderless"
        style={{ borderRadius: 12, overflow: "hidden" }}
        styles={{ header: { borderBottom: `1px solid ${token.colorBorderSecondary}`, padding: "12px 16px", minHeight: 56 }, body: { padding: 0 } }}
        title={(
          <Space size={10} align="center">
            <EnvironmentOutlined style={{ color: token.colorPrimary, fontSize: 18 }} />
            <div style={{ lineHeight: 1.25 }}>
              <div style={{ fontSize: 16, fontWeight: 600 }}>Map of Systems</div>
              <div style={{ fontSize: 12, fontWeight: 400, color: token.colorTextTertiary }}>
                Your systems and the weather stations they report to
              </div>
            </div>
          </Space>
        )}
        extra={(
          <Space size={8}>
            {data && (
              <>
                <Tag variant="filled" style={{ margin: 0 }}>{count(visible.points.length, "system")}</Tag>
                <Tag variant="filled" style={{ margin: 0 }}>{count(clustersInView.length, "station")}</Tag>
              </>
            )}
            {notPlotted > 0 && (
              <Tooltip title={`${data.outliers.length} with coordinates outside Australia${(data.byState.WA || 0) > 0 ? `, ${data.byState.WA} in WA (outside the NEM)` : ""} — not shown on the map`}>
                <Tag variant="filled" color="warning" icon={<WarningOutlined />} style={{ margin: 0 }}>{notPlotted} not plotted</Tag>
              </Tooltip>
            )}
            <Tooltip title="Reload systems and stations">
              <Button size="small" type="text" icon={<ReloadOutlined spin={loading} />} onClick={() => load(true)} disabled={!inView || loading} />
            </Tooltip>
          </Space>
        )}
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderBottom: `1px solid ${token.colorBorderSecondary}` }}>
          <Segmented size="small" options={stateOptions} value={stateFilter} onChange={setStateFilter} disabled={!data} />
          <Space size={8} wrap>
            {layerToggle(showRings, setShowRings, <AimOutlined />, `${data?.radiusKm || 20} km catchment`, "Each weather station's catchment area")}
            {layerToggle(showClouds, setShowClouds, <CloudOutlined />, "Cloud", "Himawari-9 satellite via NASA GIBS, updated every 10 minutes (visible band by day, infrared at night)")}
            {cloudFrame && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                {cloudFrame.at.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", timeZoneName: "short" })} · {cloudFrame.band}
              </Text>
            )}
          </Space>
        </div>

        <div className="mos-map" style={{ position: "relative" }}>
          <div ref={containerRef} style={{ height: MAP_HEIGHT }} />

          {/* Legend */}
          <div className="mos-panel" style={{ left: 12, bottom: 24, padding: "8px 10px", fontSize: 11, color: token.colorTextSecondary }}>
            <div style={{ fontWeight: 600, color: token.colorText, marginBottom: 4 }}>Distance to station</div>
            <div style={{ display: "grid", gridTemplateColumns: "auto auto", columnGap: 12, rowGap: 3 }}>
              {BANDS.map((b) => (
                <span key={b.label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 9, height: 9, borderRadius: "50%", background: b.colour }} />{b.label}
                </span>
              ))}
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 9, height: 9, borderRadius: "50%", background: UNASSIGNED }} />no station
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: CENTRE_INK, boxShadow: `0 0 0 2px ${SURFACE}` }} />weather station
              </span>
            </div>
          </div>

          {/* Selected station */}
          {selectedCluster && (
            <div className="mos-panel" style={{ right: 12, top: 12, padding: "8px 8px 8px 12px", display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ lineHeight: 1.3 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{selectedCluster.cluster}</div>
                <div style={{ fontSize: 11, color: token.colorTextTertiary }}>
                  {count(selectedCluster.systems, "system")} · furthest {selectedCluster.kmMax} km
                </div>
              </div>
              <Tooltip title="Show all stations">
                <Button size="small" type="text" icon={<CloseOutlined />} onClick={() => { setSelected(null); mapRef.current?.closePopup(); }} />
              </Tooltip>
            </div>
          )}

          {!selectedCluster && data && !busy && data.points.length > 0 && (
            <div className="mos-panel" style={{ right: 12, top: 12, padding: "6px 10px", fontSize: 11, color: token.colorTextTertiary }}>
              Click a system or station for weather and spot price
            </div>
          )}

          {error && (
            <div className="mos-panel" style={{ left: "50%", top: 16, transform: "translateX(-50%)", padding: "8px 12px", color: token.colorError, fontSize: 12 }}>
              {error} <Button size="small" type="link" onClick={() => load(true)}>Retry</Button>
            </div>
          )}

          <div className="mos-fade" style={{
            position: "absolute", inset: 0, zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(245,248,251,0.72)", backdropFilter: "blur(2px)",
            opacity: busy ? 1 : 0, pointerEvents: busy ? "auto" : "none",
          }}>
            <Space orientation="vertical" align="center" size={6}>
              <Spin />
              <Text type="secondary" style={{ fontSize: 12 }}>Loading your systems…</Text>
            </Space>
          </div>

          {data && !busy && data.points.length === 0 && (
            <div style={{ position: "absolute", inset: 0, zIndex: 650, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
              <div className="mos-panel" style={{ position: "static", padding: "10px 16px", color: token.colorTextTertiary }}>No systems with a location to show</div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
